/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-use-before-define */
import React, { FC, useEffect, useState, useRef } from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $wrapNodeInElement, mergeRegister } from "@lexical/utils";
import { useTranslation } from "react-i18next";
import {
  $createParagraphNode,
  $insertNodes,
  $setSelection,
  $isNodeSelection,
  $createRangeSelection,
  $isRootOrShadowRoot,
  COMMAND_PRIORITY_EDITOR,
  createCommand,
  LexicalCommand,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  DRAGOVER_COMMAND,
  DRAGSTART_COMMAND,
  DROP_COMMAND,
  $getSelection,
  $getRoot,
  $createTextNode,
  PASTE_COMMAND,
  $getNodeByKey,
} from "lexical";
import {
  $createImageNode,
  $isImageNode,
  ImageNode,
  ImagePayload,
} from "../ImageNodes/ImageNode";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/esm/Button";
import { useDebouncedCallback } from "use-debounce";
import { toBase64 } from "../../../utils/utils";

interface IResizeImageModal {
  show: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  activeEditor?: any;
  containerTarget?: HTMLElement;
  onClose: () => void;
  confirm?: (
    e: React.MouseEvent<HTMLButtonElement>,
    currentWidth?: number,
    currentHeight?: number
  ) => void;
}

const CAN_USE_DOM =
  typeof window !== "undefined" &&
  typeof window.document !== "undefined" &&
  typeof window.document.createElement !== "undefined";

// @ts-ignore
const getDOMSelection = (targetWindow) =>
  CAN_USE_DOM ? (targetWindow || window).getSelection() : null;

const TRANSPARENT_IMAGE =
  "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
const img = document.createElement("img");
img.src = TRANSPARENT_IMAGE;

// @ts-ignore
function onDragStart(event) {
  const node = getImageNodeInSelection();
  if (!node) {
    return false;
  }
  const dataTransfer = event.dataTransfer;
  if (!dataTransfer) {
    return false;
  }
  dataTransfer.setData("text/plain", "_");
  dataTransfer.setDragImage(img, 0, 0);
  dataTransfer.setData(
    "application/x-lexical-drag",
    JSON.stringify({
      data: {
        altText: node.__altText,
        caption: node.__caption,
        height: node.__height,
        key: node.getKey(),
        maxWidth: node.__maxWidth,
        showCaption: node.__showCaption,
        src: node.__src,
        width: node.__width,
      },
      type: "image",
    })
  );

  return true;
}

// @ts-ignore
function onDragover(event) {
  const node = getImageNodeInSelection();
  if (!node) {
    return false;
  }
  if (!canDropImage(event)) {
    event.preventDefault();
  }
  return true;
}

// @ts-ignore
function onDrop(event, editor) {
  const node = getImageNodeInSelection();
  if (!node) {
    return false;
  }
  const data = getDragImageData(event);
  if (!data) {
    return false;
  }
  event.preventDefault();
  if (canDropImage(event)) {
    const range = getDragSelection(event);
    node.remove();
    const rangeSelection = $createRangeSelection();
    if (range !== null && range !== undefined) {
      rangeSelection.applyDOMRange(range);
    }
    $setSelection(rangeSelection);
    editor.dispatchCommand(INSERT_IMAGE_COMMAND, data);
  }
  return true;
}

function getImageNodeInSelection() {
  const selection = $getSelection();
  if (!$isNodeSelection(selection)) {
    return null;
  }
  const nodes = selection.getNodes();
  const node = nodes[0];
  return $isImageNode(node) ? node : null;
}

// @ts-ignore
function getDragImageData(event) {
  const dragData = event.dataTransfer?.getData("application/x-lexical-drag");
  if (!dragData) {
    return null;
  }
  const { type, data } = JSON.parse(dragData);
  if (type !== "image") {
    return null;
  }

  return data;
}
// @ts-ignore
function canDropImage(event) {
  const target = event.target;
  return !!(
    target &&
    target instanceof HTMLElement &&
    // @ts-ignore
    !target.closest("code, span.editor-image") &&
    // @ts-ignore
    target.parentElement.closest("div.editor-input")
  );
}
// @ts-ignore
function getDragSelection(event) {
  let range;
  const target = event.target;
  const targetWindow =
    target == null
      ? null
      : target.nodeType === 9
      ? target.defaultView
      : target.ownerDocument.defaultView;
  const domSelection = getDOMSelection(targetWindow);
  if (document.caretRangeFromPoint) {
    range = document.caretRangeFromPoint(event.clientX, event.clientY);
  } else if (event.rangeParent && domSelection !== null) {
    domSelection.collapse(event.rangeParent, event.rangeOffset || 0);
    range = domSelection.getRangeAt(0);
  } else {
    throw Error(`Cannot get the selection when dragging`);
  }

  return range;
}

export type InsertImagePayload = Readonly<ImagePayload>;

export const INSERT_IMAGE_COMMAND: LexicalCommand<InsertImagePayload> =
  createCommand("INSERT_IMAGE_COMMAND");

export const UPDATE_IMAGE_COMMAND: LexicalCommand<InsertImagePayload> =
  createCommand("UPDATE_IMAGE_COMMAND");

export const InsertImageUriDialogBody: FC<{
  // @ts-ignore
  onClick?: ({ altText, src }) => void;
}> = ({ onClick }) => {
  const [src, setSrc] = useState("");
  const [altText, setAltText] = useState("");
  const { t } = useTranslation();
  const isDisabled = src === "";

  return (
    <>
      <input onChange={(e) => setSrc(e.target.value)} value={src} />
      <input
        placeholder=""
        onChange={(e) => setAltText(e.target.value)}
        value={altText}
      />
      <div>
        <Button
          variant="primary"
          disabled={isDisabled}
          // @ts-ignore
          onClick={() => onClick({ altText, src })}
        >
          {t("confirm")}
        </Button>
      </div>
    </>
  );
};

export const InsertImageUploadedDialogBody: FC<{
  // @ts-ignore
  onClick?: ({ altText, src }) => void;
}> = ({ onClick }) => {
  const inputUploadRef = useRef(null);
  const [altText, setAltText] = useState("");
  const { t } = useTranslation();
  const onUploadImageClick = () => {
    // @ts-ignore
    inputUploadRef.current?.click();
  };

  const onEditExcalidraw = (id: string) => {};
  // @ts-ignore
  const handleFileChange = async (event) => {
    const fileObj: File = event.target.files && event.target.files[0];
    if (!fileObj) {
      return;
    }

    // 👇️ Reset file input
    event.target.value = null;
    // 👇️ Is now empty
    // 👇️ Can still access the file object here
    // Convert to src base64 or can dispatch to API storage AWS and get the url file here.
    const srcTarget: string = (await toBase64(fileObj)) as unknown as string;
    // Insert ImageNode.
    const payload: InsertImagePayload = {
      altText: altText || fileObj.name,
      src: srcTarget,
      id: "",
      onEditExcalidraw,
    };
    // @ts-ignore
    onClick(payload);
  };

  return (
    <>
      <div className="d-flex">
        <Button variant="primary" onClick={onUploadImageClick}>
          {t("upload")}
        </Button>
        <input
          ref={inputUploadRef}
          onChange={handleFileChange}
          className="d-none"
          accept="image/*"
          type="file"
        />

        <input
          className="form-control ml-2"
          placeholder="Descriptive alternative text"
          onChange={(e) => setAltText(e.target.value)}
          value={altText}
        />
      </div>
    </>
  );
};

export const InsertImageDialog: FC<IResizeImageModal> = ({
  activeEditor,
  show,
  onClose,
  confirm,
  containerTarget,
}) => {
  const { t } = useTranslation();
  const hasModifier = useRef(false);

  useEffect(() => {
    hasModifier.current = false;
    // @ts-ignore
    const handler = (e) => {
      hasModifier.current = e.altKey;
    };
    document.addEventListener("keydown", handler);
    return () => {
      document.removeEventListener("keydown", handler);
    };
  }, [activeEditor]);

  const insertNewLine = () => {
    activeEditor.update(() => {
      const root = $getRoot();
      const p = $createParagraphNode();
      p.append($createTextNode(""));
      root.append(p);
    });
  };
  // @ts-ignore
  const onClick = (payload) => {
    insertNewLine();
    activeEditor.dispatchCommand(INSERT_IMAGE_COMMAND, payload);
    insertNewLine();
    activeEditor.focus();
    onClose();
  };

  return (
    <>
      <Modal
        size="sm"
        className="confirm-modal"
        centered
        show={show}
        container={containerTarget}
        onHide={() => onClose()}
      >
        <Modal.Body>
          <InsertImageUploadedDialogBody onClick={onClick} />
        </Modal.Body>
      </Modal>
    </>
  );
};

export default function ImagesPlugin({
  editable,
}: {
  editable?: boolean;
}): React.JSX.Element | null {
  const [editor] = useLexicalComposerContext();
  const insertNewLine = () => {
    editor.update(() => {
      const root = $getRoot();
      const p = $createParagraphNode();
      p.append($createTextNode(""));
      root.append(p);
    });
  };
  const handlePastedFile = useDebouncedCallback(async (file) => {
    const srcTarget: string = (await toBase64(file)) as unknown as string;
    const payload: InsertImagePayload = {
      altText: file.name,
      src: srcTarget,
      id: "",
    };
    insertNewLine();
    editor.dispatchCommand(INSERT_IMAGE_COMMAND, payload);
    insertNewLine();
    editor.focus();
  }, 100);

  useEffect(() => {
    if (!editor.hasNodes([ImageNode])) {
      throw new Error("ImagesPlugin: ImageNode not registered on editor");
    }

    return mergeRegister(
      editor.registerCommand(
        INSERT_IMAGE_COMMAND,
        (payload) => {
          const imageNode = $createImageNode(payload);
          $insertNodes([imageNode]);
          if ($isRootOrShadowRoot(imageNode.getParentOrThrow())) {
            $wrapNodeInElement(imageNode, $createParagraphNode).selectEnd();
          }

          return true;
        },
        COMMAND_PRIORITY_EDITOR
      ),
      editor.registerCommand(
        UPDATE_IMAGE_COMMAND,
        (payload) => {
          const imageNode = $createImageNode(payload);
          // @ts-ignore
          const node = $getNodeByKey(localStorage.getItem("node_key"));
          if (node && $isImageNode(node)) {
            node.remove();
          }
          $insertNodes([imageNode]);
          if ($isRootOrShadowRoot(imageNode.getParentOrThrow())) {
            $wrapNodeInElement(imageNode, $createParagraphNode).selectEnd();
          }

          return true;
        },
        COMMAND_PRIORITY_EDITOR
      ),

      editor.registerCommand(
        DRAGSTART_COMMAND,
        (event) => {
          return onDragStart(event);
        },
        COMMAND_PRIORITY_HIGH
      ),
      editor.registerCommand(
        DRAGOVER_COMMAND,
        (event) => {
          return onDragover(event);
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        DROP_COMMAND,
        (event) => {
          return onDrop(event, editor);
        },
        COMMAND_PRIORITY_HIGH
      ),
      // editor.registerCommand(
      //   DELETE_LINE_COMMAND,
      //   (event) => {
      //     // REPLACE NODE.
      //     // @ts-ignore
      //     const element = document.getElementById(event.excalidrawKey);

      //     const subElements = document.querySelectorAll(
      //       // @ts-ignore
      //       `[data-id='${event.excalidrawKey}']`
      //     );
      //     element?.remove();

      //     if (subElements.length > 0) {
      //       for (let index = 0; index < subElements.length; index++) {
      //         subElements[index]?.remove();
      //       }
      //     }
      //     return false;
      //   },
      //   COMMAND_PRIORITY_HIGH
      // ),
      editor.registerCommand(
        PASTE_COMMAND,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (event: any) => {
          const files = event.clipboardData?.files || event.dataTransfer?.files;
          // eslint-disable-next-line no-console
          const allow = ["image/png", "image/gif", "image/jpeg"];
          if (files.length > 0) {
            const file = files[0];
            if (allow.includes(file.type)) {
              handlePastedFile(file);
              return true;
            } else {
              return false;
            }
          } else {
            return false;
          }
        },
        COMMAND_PRIORITY_HIGH
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editable, editor]);

  return null;
}
